import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { useWaitingTimeServiceV1BatchCreateWaitingTimeRateMutation } from "_services/modules/waiting_time";
import { DurationCondition } from "_services/modules/waitingTimeRate";
import { parseFormattedNumber, yupNumTransform } from "_helper/formatters";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface WaitingTimeRateItem {
  waitingTimeRateName: string;
  durationCondition: DurationCondition;
  durationValue: number | string;
  durationMin: number | string;
  durationMax: number | string;
  rate: number | string;
  description: string;
  status: string;
}

export interface AddWaitingTimeRateFormValues {
  waitingTimeRateName: string;
  durationCondition: DurationCondition;
  durationValue: number | string;
  durationMin: number | string;
  durationMax: number | string;
  rate: number | string;
  description: string;
  status: string;
  items: WaitingTimeRateItem[];
}

const schema = yup.object({
  waitingTimeRateName: yup.string().required("Waiting Time Rate Name is required").max(100, "Max 100 characters"),
  durationCondition: yup.string().oneOf(["below", "between", "above"]).required("Duration Condition is required"),
  durationValue: yup.mixed().when("durationCondition", {
    is: (val: string) => val === "below" || val === "above",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Duration value is required").positive("Must be positive"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  durationMin: yup.mixed().when("durationCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Min value is required").min(0, "Must be >= 0"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  durationMax: yup.mixed().when("durationCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Max value is required").positive("Must be positive")
      .when("durationMin", (durationMin, schema) => {
        const minVal = Array.isArray(durationMin) ? durationMin[0] : durationMin;
        if (typeof minVal === "number" && !isNaN(minVal)) {
          return schema.moreThan(minVal, "Max must be greater than Min");
        }
        return schema;
      }),
    otherwise: () => yup.mixed().notRequired(),
  }),
  rate: yup.number().transform((value, originalValue) => {
    if (typeof originalValue === "string" && originalValue !== "") {
      return Number(originalValue.replace(/[^\d]/g, ""));
    }
    return value;
  }).typeError("Must be a number").required("Rate is required").positive("Must be positive"),
  description: yup.string().max(500, "Max 500 characters").default(""),
  status: yup.string().required("Status is required"),
  items: yup.array().of(
    yup.object({
      waitingTimeRateName: yup.string().required(),
      durationCondition: yup.string().required(),
      durationValue: yup.mixed(),
      durationMin: yup.mixed(),
      durationMax: yup.mixed(),
      rate: yup.mixed(),
      description: yup.string().default(""),
      status: yup.string().required(),
    })
  ).required(),
}).required();

const formatRate = (rate: number | string): string => {
  const num = parseFormattedNumber(rate);
  if (isNaN(num)) return "-";
  return `Rp ${new Intl.NumberFormat("id-ID").format(num)}`;
};

const useAddWaitingTimeRateForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("waitingTimeRate");

  const [batchCreateWaitingTimeRate] = useWaitingTimeServiceV1BatchCreateWaitingTimeRateMutation();

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm<AddWaitingTimeRateFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as never,
    defaultValues: {
      waitingTimeRateName: "",
      durationCondition: "below",
      durationValue: "",
      durationMin: "",
      durationMax: "",
      rate: "",
      description: "",
      status: "active",
      items: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "items",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const durationCondition = watch("durationCondition");

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const getDurationDisplayText = (item: WaitingTimeRateItem): string => {
    const fmt = (v: number | string) => typeof v === "string" ? v.replace(",", ".") : String(v);
    if (item.durationCondition === "below") {
      return `< ${fmt(item.durationValue)} Hours`;
    }
    if (item.durationCondition === "between") {
      return `${fmt(item.durationMin)} Hours - ${fmt(item.durationMax)} Hours`;
    }
    if (item.durationCondition === "above") {
      return `> ${fmt(item.durationValue)} Hours`;
    }
    return "-";
  };

  const handleAddToList = async () => {
    const fieldsToValidate: (keyof AddWaitingTimeRateFormValues)[] = [
      "waitingTimeRateName",
      "durationCondition",
      "rate",
      "status",
    ];

    if (durationCondition === "between") {
      fieldsToValidate.push("durationMin", "durationMax");
    } else {
      fieldsToValidate.push("durationValue");
    }

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const values = getValues();

    const isDuplicate = fields.some(
      (item, index) =>
        item.waitingTimeRateName.toLowerCase() === values.waitingTimeRateName.toLowerCase() &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("waitingTimeRateName", {
        type: "validate",
        message: t("error-duplicate-value"),
      });
      return;
    }

    const normalizeNum = (val: number | string) =>
      typeof val === "string" && val !== "" ? Number(val.replace(/[^\d]/g, "")) : val;

    const newItem: WaitingTimeRateItem = {
      waitingTimeRateName: values.waitingTimeRateName,
      durationCondition: values.durationCondition,
      durationValue: values.durationCondition !== "between" ? normalizeNum(values.durationValue) : "",
      durationMin: values.durationCondition === "between" ? normalizeNum(values.durationMin) : "",
      durationMax: values.durationCondition === "between" ? normalizeNum(values.durationMax) : "",
      rate: normalizeNum(values.rate),
      description: values.description,
      status: values.status,
    };

    if (editingIndex !== null) {
      update(editingIndex, newItem);
      setEditingIndex(null);
    } else {
      append(newItem);
    }

    resetForm();
  };

  const resetForm = () => {
    setValue("waitingTimeRateName", "");
    setValue("durationCondition", "below");
    setValue("durationValue", "");
    setValue("durationMin", "");
    setValue("durationMax", "");
    setValue("rate", "");
    setValue("description", "");
    setValue("status", "active");
  };

  const handleEditItem = (index: number) => {
    const item = fields[index];
    setEditingIndex(index);
    setValue("waitingTimeRateName", item.waitingTimeRateName);
    setValue("durationCondition", item.durationCondition);
    setValue("durationValue", item.durationValue);
    setValue("durationMin", item.durationMin);
    setValue("durationMax", item.durationMax);
    setValue("rate", item.rate);
    setValue("description", item.description);
    setValue("status", item.status);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      resetForm();
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    resetForm();
  };

  const handleSave = async () => {
    try {
      const payload = fields.map((f) => ({
        waiting_time_rate_name: f.waitingTimeRateName,
        duration_condition: f.durationCondition,
        duration_min: f.durationCondition === "between" ? Number(f.durationMin) : (f.durationCondition === "above" ? Number(f.durationValue) : null),
        duration_max: f.durationCondition === "between" ? Number(f.durationMax) : (f.durationCondition === "below" ? Number(f.durationValue) : null),
        rate: Number(String(f.rate).replace(/[^\d]/g, "")),
        description: f.description,
        status: f.status,
      }));
      await batchCreateWaitingTimeRate({
        waitingTimeV1BatchCreateWaitingTimeRateReq: {
          waiting_time_rates: payload.map((item) => ({
            waiting_time_rate_name: item.waiting_time_rate_name,
            duration_condition: item.duration_condition,
            duration_min: item.duration_min ?? undefined,
            duration_max: item.duration_max ?? undefined,
            rate: item.rate,
            status: item.status,
          })),
        },
      }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    if (fields.length > 0) {
      setShowCancelModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    durationCondition,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    getDurationDisplayText,
    formatRate,
    t,
  };
};

export default useAddWaitingTimeRateForm;
